import { useExportPolling } from "hooks/useExportPolling";
import { useCallback } from "react";
import {
  useDeleteModelApiV1ModelsCatalogModelsModelIdDelete,
  useListModelsApiV1ModelsCatalogGet,
  useModelsCatalogExportPostApiV1ModelsCatalogModelsModelIdExportPost,
  useUpdateModelApiV1ModelsCatalogModelsModelIdPatch,
  ListModelsApiV1ModelsCatalogGetQueryParams,
} from "vl-api/vlComponents";

export const useModelTable = (
  apiQueryParams: ListModelsApiV1ModelsCatalogGetQueryParams,
) => {
  const { startPolling, errorNotification } = useExportPolling();

  const {
    data,
    isLoading: loading,
    refetch: fetchListData,
  } = useListModelsApiV1ModelsCatalogGet(
    { queryParams: apiQueryParams },
    {
      enabled: true,
    },
  );

  const exportMutation =
    useModelsCatalogExportPostApiV1ModelsCatalogModelsModelIdExportPost();

  const deleteMutation = useDeleteModelApiV1ModelsCatalogModelsModelIdDelete();
  const updateModelMutation =
    useUpdateModelApiV1ModelsCatalogModelsModelIdPatch();

  const isDeleting = deleteMutation.isPending;
  const isUpdating = updateModelMutation.isPending;

  const tableData = data?.models || [];

  const handleExport = useCallback(
    async (modelId: string) => {
      try {
        const response = await exportMutation.mutateAsync({
          pathParams: { modelId },
        });

        const taskId =
          typeof response.id === "string" ? response.id : String(response.id);
        const datasetId = String(response.dataset_id);
        startPolling(taskId, datasetId);
      } catch (err: any) {
        errorNotification(
          err?.response?.detail || err?.message || "Failed to start export.",
        );
      }
    },
    [exportMutation, startPolling, errorNotification],
  );

  const handleDelete = useCallback(
    async (modelId: string) => {
      try {
        await deleteMutation.mutateAsync({
          pathParams: { modelId },
        });
        fetchListData();
      } catch (err: any) {
        throw err?.response?.detail || err?.error || "Failed to delete model.";
      }
    },
    [fetchListData, deleteMutation],
  );

  const handleUpdateProduction = useCallback(
    async (modelId: string, isProduction: boolean) => {
      try {
        await updateModelMutation.mutateAsync({
          pathParams: { modelId },
          body: { is_production: isProduction },
        });
        fetchListData();
      } catch (err: any) {
        throw (
          err?.response?.detail ||
          err?.error ||
          "Failed to update model production status."
        );
      }
    },
    [updateModelMutation, fetchListData],
  );

  return {
    handleExport,
    handleDelete,
    handleUpdateProduction,
    loading,
    tableData,
    fetchListData,
    isDeleting,
    isUpdating,
    totalItemsCount: data?.total,
  };
};
